import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, MapPin, Phone, Mail, Clock } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Reveal, Stagger, StaggerItem } from "@/components/Motion";
import { Seo } from "@/components/Seo";
import { useContent } from "@/lib/api";

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="card overflow-hidden">
      <button onClick={() => setOpen(!open)} aria-expanded={open}
        data-testid={`faq-item-${q.slice(0, 24).toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className="flex w-full items-center justify-between gap-6 p-6 text-left sm:p-8">
        <span className="font-serif text-lg text-ink sm:text-xl">{q}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-stone-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          <p className="px-6 pb-7 text-sm leading-relaxed text-stone-600 sm:px-8">{a}</p>
        </div>
      </div>
    </div>
  );
};

export default function Faq() {
  const { data } = useContent();
  const s = data?.settings || {};
  const c = data?.content || {};

  return (
    <div className="bg-bone">
      <Seo title="FAQs & Contact — The Owl's Nest" description="Answers about booking, vendors, and rentals at The Owl's Nest, plus how to reach us." />
      <Nav />
      <section className="mx-auto max-w-7xl px-4 pb-24 pt-32 sm:px-8 sm:pt-40 lg:px-16">
        <Reveal>
          <p className="overline-label">FAQs & Contact</p>
          <h1 className="mt-4 max-w-2xl font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            Questions, answered warmly.
          </h1>
        </Reveal>
        <div className="mt-14 grid items-start gap-12 lg:grid-cols-12">
          <Stagger className="space-y-4 lg:col-span-7">
            {(c.faqs || []).map((f) => (
              <StaggerItem key={f.q}><FaqItem q={f.q} a={f.a} /></StaggerItem>
            ))}
            <StaggerItem>
              <div className="rounded-2xl border border-dashed border-stone-300 p-6 text-sm text-stone-500 sm:p-8">
                Something else on your mind? <Link to="/inquire" className="font-semibold text-ink underline underline-offset-4">Send an inquiry</Link> and ask away.
              </div>
            </StaggerItem>
          </Stagger>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="card sticky top-28 space-y-7 p-8 sm:p-10" data-testid="contact-card">
              <div>
                <p className="overline-label">Visit us</p>
                <p className="mt-3 flex items-start gap-3 text-sm leading-relaxed text-stone-700">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {s.address}
                </p>
              </div>
              <div className="space-y-3 border-t border-black/5 pt-7">
                <a href={`tel:${(s.contact_phone || "").replace(/[^+\d]/g, "")}`} data-testid="contact-phone-link"
                  className="flex items-center gap-3 text-sm text-stone-700 transition-colors hover:text-ink">
                  <Phone className="h-4 w-4 text-gold" /> {s.contact_phone}
                </a>
                <a href={`mailto:${s.contact_email}`} data-testid="contact-email-link"
                  className="flex items-center gap-3 text-sm text-stone-700 transition-colors hover:text-ink">
                  <Mail className="h-4 w-4 text-gold" /> {s.contact_email}
                </a>
              </div>
              <div className="space-y-3 border-t border-black/5 pt-7 text-sm text-stone-700">
                <p className="flex items-start gap-3"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> Weekend and weekday-evening rentals — see the availability calendar for open times.</p>
              </div>
              <Link to="/inquire" data-testid="faq-inquire-btn" className="btn-dark w-full">
                Start an Inquiry <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
      <Footer />
    </div>
  );
}
